import { createRoute, lazyRouteComponent, stripSearchParams } from '@tanstack/react-router'
import type { ClubSort, ClubStatus } from '@volee/api-client'

import { requireBackoffice } from '../auth/guard.ts'
import { CLUB_STATUS_ORDER } from '../messages/index.ts'
import { parseOneOf, parsePage, parseQuery } from './params.ts'
import { rootRoute } from './root.tsx'

// The protected clubs list. Search, filter, sort and page are typed search params (§4.4):
// a filtered view is a link, and the back button returns to the previous result.

export interface ClubSearch {
  /** 1-based: it is the number a person shares. */
  readonly page: number
  readonly q: string | null
  readonly status: ClubStatus | null
  readonly sort: ClubSort
}

// From the ordering, not from a label map: key order in a catalogue is a translation
// detail, and the two locales are free to diverge on it.
const STATUSES = CLUB_STATUS_ORDER

/** The orderings the API accepts. The default is the server's own. */
const SORTS: readonly ClubSort[] = ['legal_name', '-legal_name', 'created_at', '-created_at']

export const DEFAULT_SORT: ClubSort = 'legal_name'

/**
 * The list with no filters. `validateSearch` returns all four fields, so every
 * `<Link to="/clubs">` must declare them: this constant is what "back to the list" means.
 */
export const DEFAULT_CLUB_SEARCH: ClubSearch = {
  page: 1,
  q: null,
  status: null,
  sort: 'legal_name',
}

export function parseStatus(value: unknown): ClubStatus | null {
  return parseOneOf(STATUSES, value)
}

export function parseSort(value: unknown): ClubSort {
  return parseOneOf(SORTS, value) ?? DEFAULT_SORT
}

/** The list as a link target: path and the search params its type requires, in one place. */
export const clubListLink = { to: '/clubs', search: DEFAULT_CLUB_SEARCH } as const

export const clubsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clubs',
  validateSearch: (search: Record<string, unknown>): ClubSearch => ({
    page: parsePage(search.page),
    q: parseQuery(search.q),
    status: parseStatus(search.status),
    sort: parseSort(search.sort),
  }),
  search: {
    // Defaults leave the URL: a colleague receives /clubs?status=active, never
    // /clubs?page=1&q=null&status=active&sort=legal_name.
    middlewares: [stripSearchParams(DEFAULT_CLUB_SEARCH)],
  },
  // Runs before the component is even fetched: without the role, no chunk downloads.
  beforeLoad: requireBackoffice,
  component: lazyRouteComponent(() => import('../pages/clubs.tsx')),
})
